import { takeEvery, call, put, select, takeLatest, fork } from 'redux-saga/effects';
import { gcApi } from '../config/axios';

import {
    FETCH_PUBLIC_USER_PROFILE,
    FETCH_USER_SOCIAL_PROFILE,
    FETCH_PROFILE_USER_PRIVACY,
    UPDATE_USER_SOCIAL_PRIVACY,
    FETCH_CONNECTION_PROFILE_STATUS,
    SAVE_SEND_CONNECTION_REQUEST,
    FETCH_PROFILE_PENDING_CONNECTIONS,
    ACCEPT_CONNECTION_REQUEST,
    DECLINE_CONNECTION_REQUEST,
    DISCONNECT_CONNECTION,
    CANCEL_CONNECTION_REQUEST,
    BLOCK_USER_REQUEST,
    UNBLOCK_USER_REQUEST,
    FETCH_USER_PERSONAL_INFO,
    FETCH_USER_DAILY_STEPS,
    RESET_PROFILE_PAGE,
    RESET_PROFILE_PAGE_REQUEST,
    PROFILE_PAGE_REPORT_USER_PROFILE_IMAGE,
    FETCH_USER_SOCIAL_PROFILE_SUCCESS,
    REMOVE_PROFILE_PAGE_ACTIVITIES_PROFILE_IMAGE,
    REMOVE_PROFILE_PAGE_COMMENTS_PROFILE_IMAGE
} from '../actions/ActionTypes';
import {
    requestPublicUserProfile,
    receivePublicUserProfile,
    failedPublicUserProfile,
    requestUserPrivacy,
    receiveUserPrivacy,
    failedUserPrivacy,
    updatePrivacy,
    receiveConnectionProfileStatus,
    failedConnectionProfileStatus,
    requestConnectionProfileStatus,
    fetchConnectionProfileStatus,
    requestPendingConnections,
    receivePendingConnections,
    requestUserPersonalInfo,
    receiveUserPersonalInfo,
    failedUserPersonalInfo,
    requestUserDailySteps,
    receiveUserDailySteps,
    failedUserDailySteps,
    resetActivities,
    resetActivityPolyline,
    resetFavoriteActivities,
    resetComments,
    resetConversation,
    resetCommentLikes,
    resetConversationLikes,
    resetGear,
    hideModal,
    resetActivities365Report,
    resetMonthlyReport,
    resetStepsReport,
    resetConnectionProfileStatus,
    resetPendingConnections,
    resetPublicUserProfile,
    resetUpcomingEvents,
    resetUserDailySteps,
    resetUserPersonalInfo,
    resetUserStatistics,
    profilePageSetReportDialog
} from '../actions';
import { handlingSuccessCallback, handlingErrorCallback } from './sagasUtils';
import { resetActivity } from '../actions/activitiesActions';
import { selectProfilePageReportDialogState } from '../reducers/pages/profilePage';
import { getPublicUserProfile } from '../reducers/user/publicUserProfile';
import { getUserSocialProfile } from '../reducers/user/userSocialProfile';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';

// Workers
export function* fetchPublicUserProfile({ displayName }) {
    try {
        yield put(requestPublicUserProfile(displayName));

        const response = yield gcApi.get(`/userprofile-service/socialProfile/public/${displayName}`);

        yield put(receivePublicUserProfile(displayName, response.data));

        const profileId = response.data?.profileId;
        yield fork(fetchProfileImageAndFrameSaga, [profileId]);
    } catch (e) {
        yield put(failedPublicUserProfile(displayName));
    }
}

export function* fetchUserPrivacy({ displayName }) {
    try {
        yield put(requestUserPrivacy(displayName));

        const response = yield gcApi.get(`/userprofile-service/socialProfile/privacy/${displayName}`);

        yield put(receiveUserPrivacy(displayName, response.data));
    } catch (e) {
        yield put(failedUserPrivacy(displayName));
    }
}

export function* updateUserPrivacy({ displayName, data, privacy }) {
    try {
        let newData = { ...data, ...privacy };
        yield put(updatePrivacy(displayName, newData));

        const response = yield gcApi.put(`/userprofile-service/socialProfile/privacy/${displayName}`, newData);

        yield put(receiveUserPrivacy(displayName, response.data));
    } catch (e) {
        yield put(failedUserPrivacy(displayName));
    }
}

export function* fetchConnectionProfileStatusSaga({ displayName }) {
    try {
        yield put(requestConnectionProfileStatus(displayName));

        const response = yield gcApi.get(
            `/userprofile-service/connection/profileStatus/${displayName}?displayMutedStatus=true`
        );

        yield put(receiveConnectionProfileStatus(displayName, response.data));
    } catch (e) {
        yield put(failedConnectionProfileStatus(displayName));
    }
}

export function* saveSendConnectionRequestSaga({ displayName, fetchStatus, callback }) {
    try {
        const response = yield gcApi.put(`/connection-service/connection/request/${displayName}`);
        if (fetchStatus) {
            yield put(fetchConnectionProfileStatus(displayName));
        }
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchPendingConnectionsSaga() {
    try {
        yield put(requestPendingConnections());
        const response = yield gcApi.get(`/userprofile-service/connection/pending`);
        yield put(receivePendingConnections(response.data));
    } catch (e) {}
}

export function* acceptConnectionRequestSaga({ connectionRequestId, displayName, fetchStatus, callback }) {
    try {
        const response = yield gcApi.put(`/connection-service/connection/accept/${connectionRequestId}`);
        if (fetchStatus) {
            yield put(fetchConnectionProfileStatus(displayName));
        }
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* declineConnectionRequestSaga({ connectionRequestId, displayName, callback }) {
    try {
        const response = yield gcApi.put(`/userprofile-service/connection/decline/${connectionRequestId}`);
        yield put(fetchConnectionProfileStatus(displayName));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {}
}

export function* disconnectConnectionSaga({ connectionRequestId, displayName }) {
    try {
        const response = yield gcApi.put(`/userprofile-service/connection/end/${connectionRequestId}`);
        yield put(fetchConnectionProfileStatus(displayName));
    } catch (e) {}
}

export function* cancelConnectionRequestSaga({ connectionRequestId, displayName }) {
    try {
        const response = yield gcApi.delete(`/userprofile-service/connection/connectionRequest/${connectionRequestId}`);
        yield put(fetchConnectionProfileStatus(displayName));
    } catch (e) {}
}

export function* blockUserRequestSaga({ displayName }) {
    try {
        const response = yield gcApi.post(`/userblock-service/blockuser/${displayName}`);
        yield put(fetchConnectionProfileStatus(displayName));
    } catch (e) {}
}

export function* unBlockUserRequestSaga({ displayName }) {
    try {
        const response = yield gcApi.delete(`/userblock-service/blockuser/${displayName}`);
        yield put(fetchConnectionProfileStatus(displayName));
    } catch (e) {}
}

export function* fetchUserPersonalInfoSaga({ displayName, includeBiometric = false, includeVO2Max = false }) {
    try {
        yield put(requestUserPersonalInfo());
        const response = yield gcApi.get(`/userprofile-service/userprofile/personal-information/${displayName}`, {
            params: {
                includeBiometric,
                includeVO2Max
            }
        });
        yield put(receiveUserPersonalInfo(response.data));
    } catch (e) {
        yield put(failedUserPersonalInfo());
    }
}

export function* fetchUserDailyStepsSaga({ displayName, startIndex }) {
    try {
        yield put(requestUserDailySteps(displayName));
        const response = yield gcApi.get(
            `/usersummary-service/usersummary/dailySummaryViews/${displayName}?start=${
                startIndex ? startIndex : 1
            }&limit=10`
        );
        yield put(receiveUserDailySteps(response.data));
    } catch (e) {
        yield put(failedUserDailySteps());
    }
}

const REMOVE_IMAGE_STATUS = [`FLAGGED_FOR_REVIEW_BY_HUMAN`, `FLAGGED_FOR_SECOND_REVIEW_BY_HUMAN`];

export function* profilePageReportUserProfileImageSaga(action) {
    const { displayName, flagReason, note } = action;
    const reportDialogState = yield select(selectProfilePageReportDialogState);
    yield put(
        profilePageSetReportDialog({
            ...reportDialogState,
            submitting: true
        })
    );
    try {
        const response = yield call(gcApi.post, `/userprofile-service/socialProfile/${displayName}/image/flag`, {
            flagReason,
            referrerContext: 'GCWeb User Profile',
            note
        });
        yield put(
            profilePageSetReportDialog({
                ...reportDialogState,
                reviewStatus: response.data.reviewStatus,
                submitting: false
            })
        );
        /** only remove image under these status */
        if (REMOVE_IMAGE_STATUS.includes(response.data.reviewStatus)) {
            const publicUserProfile = yield select(getPublicUserProfile, displayName);
            const userSocialProfile = yield select(getUserSocialProfile, displayName);
            yield put(
                receivePublicUserProfile(displayName, {
                    ...publicUserProfile,
                    profileImageUrlLarge: null,
                    profileImageUrlMedium: null,
                    profileImageUrlSmall: null,
                    profileImageUuid: null
                })
            );
            yield put({
                type: FETCH_USER_SOCIAL_PROFILE_SUCCESS,
                profile: {
                    ...userSocialProfile,
                    profileImageUrlLarge: null,
                    profileImageUrlMedium: null,
                    profileImageUrlSmall: null,
                    profileImageUuid: null
                },
                displayName
            });
            /** remove profile page feed profile images */
            yield put({
                type: REMOVE_PROFILE_PAGE_ACTIVITIES_PROFILE_IMAGE,
                displayName
            });
            /** remove profile page comments profile images */
            yield put({
                type: REMOVE_PROFILE_PAGE_COMMENTS_PROFILE_IMAGE,
                displayName
            });
        }
    } catch (error) {
        if (error.response && error.response.status === 429) {
            yield put(
                profilePageSetReportDialog({
                    ...reportDialogState,
                    reviewStatus: 'REPORTED',
                    submitting: false
                })
            );
        } else {
            yield put(
                profilePageSetReportDialog({
                    ...reportDialogState,
                    submitting: false
                })
            );
        }
    }
}

export function* resetProfilePageSaga({}) {
    try {
        yield put(resetActivity());
        yield put(resetActivities());
        yield put(resetActivityPolyline());
        yield put(resetFavoriteActivities());
        yield put(resetComments());
        yield put(resetConversation());
        yield put(resetCommentLikes());
        yield put(resetConversationLikes());
        yield put(resetGear());
        yield put(hideModal());
        yield put(resetActivities365Report());
        yield put(resetMonthlyReport());
        yield put(resetStepsReport());
        yield put(resetConnectionProfileStatus());
        yield put(resetPendingConnections());
        yield put(resetPublicUserProfile());
        yield put(resetUpcomingEvents());
        yield put(resetUserDailySteps());
        yield put(resetUserPersonalInfo());
        yield put(resetUserStatistics());
        yield put({ type: RESET_PROFILE_PAGE });
    } catch (e) {}
}

function* fetchProfileImageAndFrameSaga(userIds) {
    if (!userIds || !userIds.length) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

export const userProfileSagas = [
    takeEvery(FETCH_PUBLIC_USER_PROFILE, fetchPublicUserProfile),
    takeEvery(FETCH_PROFILE_USER_PRIVACY, fetchUserPrivacy),
    takeEvery(UPDATE_USER_SOCIAL_PRIVACY, updateUserPrivacy),
    takeEvery(FETCH_CONNECTION_PROFILE_STATUS, fetchConnectionProfileStatusSaga),
    takeEvery(SAVE_SEND_CONNECTION_REQUEST, saveSendConnectionRequestSaga),
    takeEvery(FETCH_PROFILE_PENDING_CONNECTIONS, fetchPendingConnectionsSaga),
    takeEvery(ACCEPT_CONNECTION_REQUEST, acceptConnectionRequestSaga),
    takeEvery(DECLINE_CONNECTION_REQUEST, declineConnectionRequestSaga),
    takeEvery(DISCONNECT_CONNECTION, disconnectConnectionSaga),
    takeEvery(CANCEL_CONNECTION_REQUEST, cancelConnectionRequestSaga),
    takeEvery(BLOCK_USER_REQUEST, blockUserRequestSaga),
    takeEvery(UNBLOCK_USER_REQUEST, unBlockUserRequestSaga),
    takeEvery(FETCH_USER_PERSONAL_INFO, fetchUserPersonalInfoSaga),
    takeEvery(FETCH_USER_DAILY_STEPS, fetchUserDailyStepsSaga),
    takeLatest(PROFILE_PAGE_REPORT_USER_PROFILE_IMAGE, profilePageReportUserProfileImageSaga),
    takeEvery(RESET_PROFILE_PAGE_REQUEST, resetProfilePageSaga)
];
